import * as moment from "moment-timezone";

export type TimeFormatPreference = "12h" | "24h";

export const TIME_FORMAT_STORAGE_KEY = "time-format-preference";
export const TIME_FORMAT_CHANGE_EVENT = "time-format-change";

export function getUserTimeFormatPreference(): TimeFormatPreference {
  const value = localStorage.getItem(TIME_FORMAT_STORAGE_KEY);
  return value === "12h" ? "12h" : "24h";
}

export function setUserTimeFormatPreference(value: TimeFormatPreference): void {
  localStorage.setItem(TIME_FORMAT_STORAGE_KEY, value);
  window.dispatchEvent(new CustomEvent(TIME_FORMAT_CHANGE_EVENT, { detail: value }));
}

export function getPreferredFormat(format24: string, format12: string): string {
  return getUserTimeFormatPreference() === "12h" ? format12 : format24;
}

export function formatMomentWithUserPreference(
  dateInput: moment.MomentInput,
  format24: string,
  format12: string
): string {
  const format = getPreferredFormat(format24, format12);
  return moment(dateInput).format(format);
}

export function toDisplayTime(value: string, format: TimeFormatPreference = getUserTimeFormatPreference()): string {
  if (!value) {
    return "";
  }

  const parsed = moment(value, ["HH:mm", "H:mm", "hh:mm A", "h:mm A"], true);
  if (!parsed.isValid()) {
    return value;
  }

  return format === "12h" ? parsed.format("hh:mm A") : parsed.format("HH:mm");
}

export function parseDisplayTimeTo24h(
  value: string,
  format: TimeFormatPreference = getUserTimeFormatPreference()
): string | null {
  if (!value) {
    return "";
  }

  const normalized = value.trim().toUpperCase();
  const formats =
    format === "12h"
      ? ["hh:mm A", "h:mm A", "hh:mmA", "h:mmA", "HH:mm", "H:mm"]
      : ["HH:mm", "H:mm", "hh:mm A", "h:mm A", "hh:mmA", "h:mmA"];

  const parsed = moment(normalized, formats, true);
  if (!parsed.isValid()) {
    return null;
  }

  return parsed.format("HH:mm");
}

export type TypedTimeNormalizationResult = {
  display: string;
  canonical: string | null;
};

export function normalizeTypedTimeInput(
  value: string,
  format: TimeFormatPreference = getUserTimeFormatPreference()
): TypedTimeNormalizationResult {
  const raw = (value || "").toUpperCase().replace(/[^0-9APM:]/g, "").trim();

  if (!raw) {
    return { display: "", canonical: "" };
  }

  const parsedFromText = moment(
    raw,
    ["HH:mm", "H:mm", "hh:mm A", "h:mm A", "hh:mmA", "h:mmA"],
    true
  );

  if (parsedFromText.isValid()) {
    const canonical = parsedFromText.format("HH:mm");
    return {
      display: toDisplayTime(canonical, format),
      canonical,
    };
  }

  const digits = raw.replace(/\D/g, "").slice(0, 4);
  if (!digits) {
    return { display: "", canonical: "" };
  }

  if (digits.length <= 2) {
    return { display: digits, canonical: null };
  }

  if (digits.length === 3) {
    return {
      display: `${digits.slice(0, 2)}:${digits.slice(2)}`,
      canonical: null,
    };
  }

  const h24 = Number(digits.slice(0, 2));
  const m = Number(digits.slice(2, 4));
  if (Number.isNaN(h24) || Number.isNaN(m) || h24 > 23 || m > 59) {
    return {
      display: `${digits.slice(0, 2)}:${digits.slice(2, 4)}`,
      canonical: null,
    };
  }

  const canonical = `${h24.toString().padStart(2, "0")}:${m
    .toString()
    .padStart(2, "0")}`;

  return {
    display: toDisplayTime(canonical, format),
    canonical,
  };
}
